import { Box, IconButton, CircularProgress } from "@mui/material";
import { ArrowForward, SaveOutlined as SaveIcon } from "@mui/icons-material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { t } from "../componenets/dashboard/coach/coachTokens";
import useIsMobile from "../hooks/useIsMobile";
import { RTL } from "../cache/CacheRtl";
import CalculatorPanel from "../componenets/nutrition/v2/calculator/CalculatorPanel";
import {
  defaultCalculatorState,
  deriveTargets,
  hasClientData,
  isCalculable,
  type CalculatorState,
} from "../componenets/nutrition/v2/calculator/calculatorState";
import { buildTraineePrefill } from "../componenets/nutrition/v2/calculator/traineePrefill";
import TargetsSummaryBar from "../componenets/nutrition/v2/builder/TargetsSummaryBar";
import TargetsMiniStrip from "../componenets/nutrition/v2/builder/TargetsMiniStrip";
import MealsList from "../componenets/nutrition/v2/builder/MealsList";
import { useStickyHeaderHeight } from "../componenets/nutrition/v2/builder/useStickyHeaderHeight";
import { useScrollPageToTop } from "../componenets/nutrition/v2/builder/useScrollPageToTop";
import MacroGoalsView from "../componenets/nutrition/v2/builder/MacroGoalsView";
import MenuPdfSection from "../componenets/nutrition/v2/builder/MenuPdfSection";
import MenuNotes from "../componenets/nutrition/v2/builder/MenuNotes";
import SupplementsSection from "../componenets/nutrition/v2/builder/SupplementsSection";
import WaterGoalSection from "../componenets/nutrition/v2/builder/WaterGoalSection";
import ConfirmDialog from "../componenets/nutrition/v2/dialogs/ConfirmDialog";
import { BuilderToast, Card, InlineEdit, PrimaryButton, TextButton } from "../componenets/nutrition/v2/ui/primitives";
import MenuTypeChip from "../componenets/nutrition/v2/ui/MenuTypeChip";
import { v2, nums } from "../componenets/nutrition/v2/ui/v2Tokens";
import { energyFigure, macroFigure } from "../componenets/nutrition/v2/ui/nutritionUnits";
import { useNutritionDisplayMode } from "../contexts/NutritionDisplayContext";
import { saveMenu } from "../services/NutritionV2Service";
import { useMenuV2 } from "../componenets/nutrition/v2/data/useNutritionV2";
import {
  createMenu,
  dayTotals,
  isMealBuilderType,
  sameMacros,
  targetsFromMeals,
  MENU_TYPE_LABELS,
  type DayOfWeek,
  type MacroSet,
  type MenuV2,
} from "../componenets/nutrition/v2/model/nutritionV2Model";

const DAYS: Array<{ value: DayOfWeek; label: string }> = [
  { value: "SUNDAY", label: "א׳" },
  { value: "MONDAY", label: "ב׳" },
  { value: "TUESDAY", label: "ג׳" },
  { value: "WEDNESDAY", label: "ד׳" },
  { value: "THURSDAY", label: "ה׳" },
  { value: "FRIDAY", label: "ו׳" },
  { value: "SATURDAY", label: "ש׳" },
];

const f1 = (n: number) => Math.round(n * 10) / 10;

/**
 * The targets as figures and their units, in the company's unit. Number and unit come from one
 * helper call, so a points figure can never sit beside `גר׳`. Fibre has no point value and stays
 * in grams in both modes.
 */
const GoalFigures = ({ targets }: { targets: MacroSet }) => {
  const mode = useNutritionDisplayMode();
  const energy = energyFigure(targets.calories, mode);
  const [protein, carbs, fats] = (["protein", "carbs", "fats"] as const).map((key) =>
    macroFigure(key, targets[key], mode)
  );
  return (
    <>
      <b>{energy.text}</b> {energy.unit} · <b>{protein.text}</b> {protein.unit} חלבון ·{" "}
      <b>{carbs.text}</b> {carbs.unit} פחמימות · <b>{fats.text}</b> {fats.unit} שומן ·{" "}
      <b>{f1(targets.fiber)}</b> גר׳ סיבים
    </>
  );
};

type EditorProps = {
  userId: string;
  /** absent for a new menu */
  existing?: MenuV2;
  /**
   * A company template to start from. Only the id travels: the SERVER deep-copies the template's
   * meals when the menu is created, so the builder never holds a half-copy that a failed save
   * would leave stranded.
   */
  templateId: string | null;
};

const MenuBuilderV2Editor = ({ userId, existing, templateId }: EditorProps) => {
  const navigate = useNavigate();
  /**
   * Structural, not cosmetic — which is the bar for reaching for this rather than a responsive
   * `sx` value. The menu-type chip and the day toggles are rendered in a DIFFERENT PLACE in the
   * tree on a phone (out of the sticky band, into a settings row below it), and the mini strip
   * exists only there. Rendering both and hiding one would duplicate every label and every `role`
   * in the accessibility tree, and the existing suite's `getByRole` queries would find two.
   */
  const isMobile = useIsMobile();

  const [menu, setMenu] = useState<MenuV2>(() => existing || createMenu(userId));
  // the menu TYPE is chosen at the end of the calculator, not before it
  const showsMealBuilder = isMealBuilderType(menu.menuType);
  const isPdfMenu = menu.menuType === "PDF_PLAN";
  const [calculator, setCalculator] = useState<CalculatorState>(
    () => (existing?.calculator as CalculatorState) || defaultCalculatorState()
  );

  /**
   * This menu was built WITHOUT a calculation, so its own foods are the recommendation: the
   * targets are kept equal to what is in the menu as the trainer builds it, and nothing ever
   * reads as over or under.
   *
   * Read off the SAVED calculator, not the panel's live state, so reopening the menu resumes the
   * same behaviour — and so a menu written before any of this existed (no stored calculator at
   * all, or one with no such flag) keeps its calculated targets untouched.
   *
   * Only ever true where the menu HAS contents to follow. A macro or PDF menu created without a
   * calculator carries the same flag — the trainer skipped the same screen — but there the targets
   * are the menu, typed by hand off the file or into the goals view, and following a menu with no
   * meals in it would hold them at zero.
   */
  const targetsFollowMenu =
    showsMealBuilder && (menu.calculator as CalculatorState | null)?.skipped === true;
  // step 2 shows up once the trainer confirms the calculator
  const [builderOpen, setBuilderOpen] = useState(Boolean(existing));
  const [calculatorOpen, setCalculatorOpen] = useState(!existing);
  // what the calculator looked like when it was reopened, so "ביטול" can restore it
  const [calculatorSnapshot, setCalculatorSnapshot] = useState<CalculatorState | null>(null);
  const [notice, setNotice] = useState("");

  // The page header sticks at the top; everything that sticks beneath it needs its height, and
  // that height changes as the header's rows wrap. Measured, not assumed.
  const [headerRef, headerHeight] = useStickyHeaderHeight();
  const [pageRef, scrollToTop] = useScrollPageToTop();

  /**
   * Prefill from the trainee's real data: profile + goal/weekly range, target steps, and the
   * active program's sessions-per-week and average length.
   *
   * Runs for an EXISTING menu too, whenever that menu carries no client data of its own. It used
   * to skip every saved menu, on the assumption that a saved menu always brings its calculator
   * back — but a menu created before the calculator was persisted, one copied from a template, or
   * one duplicated has nothing stored, and "ערוך מחשבון" then opened a blank panel showing only
   * placeholders (the greyed 35 / 75 / 175) with every required field flagged red. The trainee's
   * age and weight were in their profile the whole time.
   *
   * A stored calculator always wins: this only ever fills a panel that has nothing in it, and the
   * `hasClientData` re-check inside the setter means a trainer who started typing while the three
   * requests were in flight keeps what they typed.
   */
  useEffect(() => {
    if (!userId || hasClientData(calculator)) return undefined;
    let cancelled = false;
    buildTraineePrefill(userId)
      .then(({ state }) => {
        if (cancelled) return;
        setCalculator((prev) => (hasClientData(prev) ? prev : { ...state, skipped: prev.skipped }));
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
    // Once per menu: `calculator` is read here but must not re-trigger this, or every keystroke
    // that empties the weight field would fire three requests.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  /**
   * Follow mode's one job: keep the targets equal to what is in the menu.
   *
   * Written into `menu.targets` rather than merely displayed, so that what the trainee's app
   * receives is a real set of numbers — a menu whose targets were left at zero would draw every
   * ring as infinitely over. The equality guard is what keeps opening such a menu from looking
   * like an edit: the stored targets already equal the totals, so there is nothing to set.
   */
  useEffect(() => {
    if (!targetsFollowMenu) return;
    setMenu((prev) => {
      const fromMeals = targetsFromMeals(prev);
      return sameMacros(prev.targets, fromMeals) ? prev : { ...prev, targets: fromMeals };
    });
  }, [targetsFollowMenu, menu.meals]);

  const derived = useMemo(() => deriveTargets(calculator), [calculator]);
  const totals = useMemo(() => dayTotals(menu), [menu]);

  /**
   * The trainee's weight, for the one figure a coach reads a protein target in: grams per kilo.
   *
   * Off the calculator, which is where this app already keeps the trainee's body data — prefilled
   * from their profile even on a menu that carries no calculation of its own. Zero when nobody has
   * said, and the bar then simply omits the line rather than dividing by a number it invented.
   */
  const bodyWeightKg = Number(calculator.weight) || 0;

  /**
   * The calculator does NOT continuously overwrite `menu.targets`. It is a recommendation:
   * the trainer can edit the targets of a macro / PDF menu by hand, and those edits are
   * the truth. The calculator only writes into the menu when it is explicitly confirmed
   * (creating the menu, or "החל יעדים" after reopening it).
   */
  const applyCalculatorTargets = useCallback(
    // `skipped: false` is written explicitly, not left out: this is also the way OUT of follow
    // mode, and a menu that was created without a calculator has to stop tracking its meals the
    // moment the trainer gives it a calculation.
    (prev: MenuV2): MenuV2 => ({
      ...prev,
      targets: derived.targets,
      calculator: { ...calculator, skipped: false },
    }),
    [derived.targets, calculator]
  );

  /**
   * The other way to answer "what should this menu aim at": whatever is in it.
   *
   * Used both by the trainer who walks past the calculator on the way in and by the one who
   * decides, halfway through building, that the targets are not the point — from then on the
   * menu's own contents are the recommendation and follow every edit.
   *
   * The menu's STORED calculator is kept, not the panel's live state: a trainer who opened the
   * calculator, changed a number and closed it without applying has not applied it, and this must
   * not be the write that does. Only on a menu that has none — a new one, or one saved before the
   * calculator was persisted — does the panel's state (which is the trainee's prefill) fill in, so
   * that "פתח מחשבון" later still knows who this menu is for.
   */
  const withMenuAsTargets = useCallback(
    (prev: MenuV2): MenuV2 => ({
      ...prev,
      targets: targetsFromMeals(prev),
      calculator: { ...((prev.calculator as CalculatorState | null) ?? calculator), skipped: true },
    }),
    [calculator]
  );

  // Saving is explicit — the trainer decides when the menu is committed. An EXISTING menu
  // is baselined at its stored state, otherwise nothing in it could ever be saved: the
  // snapshot would stay null and `isDirty` would be permanently false.
  const [savedSnapshot, setSavedSnapshot] = useState<string | null>(() =>
    existing ? JSON.stringify(existing) : null
  );

  /**
   * The template seeds the menu ONCE, at creation, and is then spent.
   *
   * `templateId` comes from the URL and stays there for the life of the tab, so sending it on
   * every save was sending it on every save that still took the CREATE path — and the server
   * answers a create carrying a template id with the TEMPLATE's meals, ignoring the payload's.
   * A trainer whose first save failed, then built the menu out and pressed "שמור תפריט", had
   * their work replaced by the template's contents. Reported as "it resets my foods".
   */
  const [pendingTemplateId, setPendingTemplateId] = useState<string | null>(templateId);
  const spendTemplate = (saved: MenuV2) => {
    if (saved.id) setPendingTemplateId(null);
  };
  const isDirty = builderOpen && savedSnapshot !== null && JSON.stringify(menu) !== savedSnapshot;

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");

  const saveWholeMenu = async () => {
    /*
     * The note carries its own date, because the trainee's app prints it beside the text —
     * a note with no date reads as timeless advice rather than as something said this week.
     * Stamped only when the TEXT changed: re-saving a menu for an unrelated edit must not
     * make last month's note look like today's.
     */
    setSaving(true);
    setSaveError("");
    try {
      // The server returns the stored menu, ids and all, and the builder ADOPTS it wholesale rather
      // than keeping what it sent. That is what turns the client's temporary uids into real ids in
      // one step — and it is why the note's date is now the server's to decide, not ours.
      const saved = await saveMenu(menu, pendingTemplateId || undefined);
      setMenu(saved);
      spendTemplate(saved);
      setSavedSnapshot(JSON.stringify(saved));
      setNotice("התפריט נשמר");
    } catch (e) {
      // Never silently: the trainer has just spent ten minutes on this and the only copy of it is
      // in this tab. Leaving `savedSnapshot` alone keeps the menu dirty so it can be saved again.
      setSaveError("השמירה נכשלה. התפריט לא נשמר — נסה שוב.");
    } finally {
      setSaving(false);
    }
  };

  // Leaving with unsaved work must not happen silently — saving is explicit, so ANY way out of
  // this page can lose a whole menu in a single click.
  const [leaveOpen, setLeaveOpen] = useState(false);
  const menusPath = `/app/clients/${userId}/nutrition-v2`;
  /**
   * Where the trainer was heading when we stopped them. The back arrow means the menus list;
   * a click in the sidebar means wherever that link pointed. Remembering it is what lets
   * "צא בלי לשמור" finish the journey they actually started.
   */
  const [leaveTo, setLeaveTo] = useState<string | null>(null);
  const leaveNow = useCallback(
    () => navigate(leaveTo || menusPath),
    [navigate, leaveTo, menusPath]
  );
  const backToMenus = useCallback(() => navigate(menusPath), [navigate, menusPath]);
  const requestLeave = () => {
    setLeaveTo(menusPath);
    if (isDirty) setLeaveOpen(true);
    else backToMenus();
  };

  /**
   * The same question for every other way out.
   *
   * The guard used to sit on the back arrow alone, so a trainer who clicked anything in the
   * sidebar — or closed the tab — lost the menu with no warning at all. The back arrow is simply
   * the exit we happened to own.
   *
   * In-app links are caught in the CAPTURE phase, before react-router's own handler runs, which
   * is the only place a <Link> can still be stopped: this app mounts a plain <BrowserRouter>, and
   * `useBlocker` needs a data router. `beforeunload` covers closing the tab, reloading, and typing
   * a new address — there the browser asks in its own words, which we cannot style and should not
   * try to.
   */
  useEffect(() => {
    if (!isDirty) return undefined;

    const onClickCapture = (event: MouseEvent) => {
      // let the trainer keep using modifier-clicks to open things in a new tab; that leaves
      // this page exactly where it is, so there is nothing to warn about
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey
          || event.shiftKey || event.altKey) {
        return;
      }
      const anchor = (event.target as HTMLElement | null)?.closest?.("a[href]") as HTMLAnchorElement | null;
      if (!anchor || anchor.target === "_blank") return;

      const href = anchor.getAttribute("href") || "";
      // in-app routes only — an external link or a mailto: is the browser's business
      if (!href.startsWith("/")) return;
      const destination = href.split("#")[0];
      if (destination === window.location.pathname) return;

      event.preventDefault();
      event.stopPropagation();
      setLeaveTo(destination);
      setLeaveOpen(true);
    };

    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      // Chrome still wants returnValue set, even though it shows its own wording
      event.returnValue = "";
    };

    document.addEventListener("click", onClickCapture, true);
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => {
      document.removeEventListener("click", onClickCapture, true);
      window.removeEventListener("beforeunload", onBeforeUnload);
    };
  }, [isDirty]);

  /**
   * The calculator's exit: pick which kind of menu these targets become.
   *
   * This is where a NEW menu is first written to the server, so it is also where the template (if
   * any) is applied — the server copies its meals in and hands back the result.
   */
  const createMenuStep = useCallback(
    async (menuType: MenuV2["menuType"], options: { skipCalculator?: boolean } = {}) => {
      // A calculator that was skipped outright and one that was never filled in mean the same
      // thing — the trainer is not working from a calculation — so they get the same menu.
      const skipped = Boolean(options.skipCalculator) || !isCalculable(calculator);
      const next: MenuV2 = {
        ...(skipped ? withMenuAsTargets(menu) : applyCalculatorTargets(menu)),
        menuType,
        // a still-unnamed menu takes the name of the kind of menu it became
        name:
          menu.name === "תפריט חדש" && menuType !== "DETAILED_PLAN"
            ? MENU_TYPE_LABELS[menuType]
            : menu.name,
      };
      setMenu(next);
      setBuilderOpen(true);
      setCalculatorOpen(false);
      setSaveError("");
      try {
        const saved = await saveMenu(next, pendingTemplateId || undefined);
        setMenu(saved);
        spendTemplate(saved);
        setSavedSnapshot(JSON.stringify(saved));
      } catch (e) {
        // The builder still opens with the targets the trainer just confirmed — losing those to a
        // network error would send them back through the whole calculator. It is simply unsaved,
        // which the header already says, and "שמור תפריט" retries.
        setSaveError("לא הצלחנו ליצור את התפריט בשרת. השינויים לא נשמרו — נסה לשמור שוב.");
        setSavedSnapshot(JSON.stringify(existing || {}));
      }
    },
    [menu, calculator, applyCalculatorTargets, withMenuAsTargets, pendingTemplateId, existing]
  );

  // The link that reopens the calculator lives in the sticky targets bar, so it is on screen from
  // anywhere in the menu — while the calculator it opens is at the top of the page. Without this,
  // pressing it from halfway down a menu looks like it did nothing.
  const openCalculator = () => {
    setCalculatorSnapshot(calculator);
    setCalculatorOpen(true);
    scrollToTop();
  };

  // confirming the reopened calculator is the one place it overwrites hand-set targets
  const applyCalculator = () => {
    setMenu(applyCalculatorTargets);
    setCalculatorSnapshot(null);
    setCalculatorOpen(false);
  };

  const cancelCalculator = () => {
    if (calculatorSnapshot) setCalculator(calculatorSnapshot);
    setCalculatorSnapshot(null);
    setCalculatorOpen(false);
  };

  /**
   * "Stop counting me against a number" — the same state a trainer gets by walking past the
   * calculator, reachable from inside a menu that already has one.
   *
   * A MODE rather than a one-shot: the targets do not merely take the menu's current contents, they
   * become them, and go on following every later edit. A trainer who has decided the calculator is
   * not what this menu is about should not have to press this again after each food.
   *
   * The way back is the calculator itself — confirming it hands the targets back to the
   * recommendation. See `applyCalculatorTargets`.
   */
  const ignoreTargets = () => {
    setMenu(withMenuAsTargets);
    setNotice("היעדים ייגזרו מעכשיו מהתפריט עצמו");
  };

  const toggleDay = (day: DayOfWeek) =>
    setMenu((prev) => ({
      ...prev,
      applicableDays: prev.applicableDays.includes(day)
        ? prev.applicableDays.filter((d) => d !== day)
        : [...prev.applicableDays, day],
    }));

  /* The targets as one line. Shared by the macro grid's head and — when there is no macro
     grid — the standalone card above it, so the two can never disagree. */
  const goalSummary = (
    <Box
      component="span"
      data-testid="targets-goal-summary"
      // the figures are the content and the words are the labels, so the figures carry the
      // size and the colour — at one weight the whole line reads as undifferentiated grey
      sx={{ "& b": { fontSize: "14px", fontWeight: 600, color: v2.ink, letterSpacing: "-.01em" } }}
    >
      <GoalFigures targets={menu.targets} />
    </Box>
  );

  const editCalculatorLink = (
    <TextButton
      quiet
      tone="green"
      onClick={openCalculator}
      sx={{ height: { xs: "40px", md: "30px" }, padding: "0 8px", fontSize: "13px" }}
    >
      {/* A menu built without one has no calculation to "edit" — the same door, named for what
          is behind it in each case. */}
      {targetsFollowMenu || !menu.calculator ? "פתח מחשבון" : "ערוך מחשבון"}
    </TextButton>
  );

  /**
   * The head of the targets bar: where the recommendation came from, and how to change that.
   *
   * In follow mode there is nothing to press — the targets already are the menu — so the chip
   * says so, and the only offer is the calculator. Otherwise the trainer can hand the goal over
   * to what they have actually built, which is only an offer worth making once there is something
   * in the menu to take.
   */
  const targetsActions = (
    <Box sx={{ display: "flex", alignItems: "center", gap: "6px", flexWrap: "wrap" }}>
      {targetsFollowMenu ? (
        <Box
          data-testid="targets-from-menu-chip"
          title="הערכים המומלצים הם מה שיש בתפריט. לחזרה ליעדים מחושבים — פתח מחשבון והחל יעדים"
          sx={{
            fontSize: "11px",
            fontWeight: 700,
            whiteSpace: "nowrap",
            padding: "2px 10px",
            borderRadius: "20px",
            color: v2.brand,
            background: v2.brandWash,
          }}
        >
          נגזר מהתפריט
        </Box>
      ) : (
        showsMealBuilder && (
          <TextButton
            quiet
            onClick={ignoreTargets}
            data-testid="ignore-targets"
            title="התפריט יפסיק להיספר מול היעדים — הערכים המומלצים יהיו מה שיש בו"
            sx={{ height: { xs: "40px", md: "30px" }, padding: "0 8px", fontSize: "13px" }}
          >
            התעלם מיעדים
          </TextButton>
        )
      )}
      {editCalculatorLink}
    </Box>
  );

  /* Read-only on purpose: a menu is activated or paused from the menus list, where the trainer
     sees it next to the other menus of the same trainee. Shown here only when it is paused, so it
     is clear why the trainee does not see this menu. */
  const inactiveChip = !menu.active && (
    <Box
      title="ניתן להפעיל את התפריט מרשימת התפריטים"
      sx={{
        fontSize: "12px",
        fontWeight: 500,
        whiteSpace: "nowrap",
        padding: "3px 10px",
        borderRadius: "20px",
        color: v2.over,
        background: v2.overWash,
      }}
    >
      לא פעיל
    </Box>
  );

  /**
   * The kind of menu — stated, not chosen.
   *
   * This used to be a segmented control that switched between the three types at any time, in both
   * directions. The type is what the menu IS: a PDF menu is its file, a macro menu is its numbers,
   * a detailed menu is its meals. It is decided once, on the way out of the calculator, and a menu
   * that is the wrong kind is created again as the right one — the same chip the menus list shows,
   * with no way to press it.
   */
  const menuTypeChip = builderOpen && (
    <MenuTypeChip
      menuType={menu.menuType}
      title="סוג התפריט נקבע בעת היצירה ואינו ניתן לשינוי"
    />
  );

  /* which days of the week this menu applies to */
  const daysControl = (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "8px",
        flexWrap: "wrap",
        marginInlineStart: { xs: 0, md: "auto" },
      }}
    >
      <Box
        component="span"
        id="menu-days-label"
        sx={{ fontSize: "12.5px", color: v2.ink3, whiteSpace: "nowrap" }}
      >
        ימים בשבוע
      </Box>
      <Box role="group" aria-labelledby="menu-days-label" sx={{ display: "flex", gap: "3px", flexWrap: "wrap" }}>
        {DAYS.map((d) => {
          const on = menu.applicableDays.includes(d.value);
          return (
            <Box
              key={d.value}
              component="button"
              type="button"
              aria-label={`יום ${d.label}`}
              aria-pressed={on}
              onClick={() => toggleDay(d.value)}
              sx={{
                // 7 × 38 plus six 3px gaps is 284, which still fits the 351px a 375 phone has
                width: { xs: "38px", md: "26px" },
                height: { xs: "38px", md: "26px" },
                borderRadius: v2.rSm,
                border: `1px solid ${on ? v2.brand : v2.line}`,
                background: on ? v2.brand : v2.surface,
                color: on ? "#fff" : v2.ink2,
                fontSize: "12px",
                fontWeight: on ? 600 : 500,
                fontFamily: "inherit",
                cursor: "pointer",
                transition: "all .12s",
                "&:hover": on ? {} : { borderColor: v2.brand, color: v2.brand },
              }}
            >
              {d.label}
            </Box>
          );
        })}
      </Box>
    </Box>
  );

  return (
    <RTL>
      {/* The whole builder is capped at a readable column width — a menu is read top to
          bottom, and stretching the calculator and the meals across a wide screen makes
          both harder to scan. */}
      <Box
        ref={pageRef}
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "14px",
          paddingBottom: "60px",
          maxWidth: "1200px",
          marginInline: "auto",
          width: "100%",
        }}
      >
        {/*
          The header sticks, because the two things it carries — whether there is anything
          unsaved, and the button that saves it — are needed at every scroll position of a
          menu that can run to a dozen meals.

          ONE row, kept as short as it can be: everything below it sticks too, so every pixel
          the header spends is a pixel of menu that is never visible.

          ── on a phone ──

          The row wraps, and at 375px it wrapped to FOUR — about 173px of sticky chrome, on top of
          which the targets bar pinned another ~300. Between them they took the whole screen. So
          below `md` the band keeps only what has to be readable at every scroll position — who
          this menu is and whether it is saved — and hands the rest to the settings row underneath,
          which scrolls away. What is left is the mini strip: the one number a trainer is actually
          building against.

          The controls MOVE rather than being rendered twice and hidden with `display`. Two copies
          would put two `role="group"`s named "ימים בשבוע" in the accessibility tree, and every
          `getByRole` in the suite would find both.
        */}
        <Box
          ref={headerRef}
          data-testid="menu-header"
          sx={{
            position: "sticky",
            top: 0,
            zIndex: 30,
            /* The bleed only does anything while the page is narrower than its 1200px cap, and on
               a phone `<main>`'s `overflowX: hidden` clips it entirely — so it is simply off there.
               Above md it keeps the 24px it has always had: it is 4px short of the layout gutter,
               but correcting that would move the desktop header, which is not this change's job. */
            marginInline: { xs: 0, md: "-24px" },
            paddingInline: { xs: 0, md: "24px" },
            background: "rgba(246,248,250,.88)",
            backdropFilter: "saturate(180%) blur(12px)",
            borderBottom: `1px solid ${v2.line}`,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: { xs: "8px", md: "12px" },
              rowGap: "8px",
              padding: { xs: "6px 0", md: "9px 0" },
              flexWrap: "wrap",
            }}
          >
            <IconButton
              aria-label="חזרה"
              onClick={requestLeave}
              sx={{
                color: v2.ink3,
                p: { xs: 1, md: 0.5 },
                borderRadius: v2.rSm,
                flex: { xs: "none", md: "0 1 auto" },
                "&:hover": { bgcolor: v2.lineSoft, color: v2.ink },
              }}
            >
              <ArrowForward sx={{ fontSize: 19 }} />
            </IconButton>

            <InlineEdit
              size="md"
              ariaLabel="שם התפריט"
              value={menu.name}
              onChange={(name) => setMenu((prev) => ({ ...prev, name }))}
              // on a phone the name takes the room the day buttons used to, so a long menu name is
              // readable instead of being capped at a third of the row
              sx={{ maxWidth: { xs: "none", md: "220px" }, flex: { xs: "1 1 auto", md: "0 1 auto" }, minWidth: 0 }}
            />

            {!isMobile && inactiveChip}
            {!isMobile && menuTypeChip}
            {!isMobile && daysControl}

            {builderOpen && (
              <>
                {/* renaming the menu, editing a meal or moving a slider all land here — an
                    amber dot next to the save button makes "there is something to save"
                    hard to miss without shouting */}
                <Box
                  /* Below md the WORDS are hidden and the dot carries the meaning, so the sentence
                     moves into a label. Above md the words are visible and the element is left
                     exactly as it was — no role, no label, nothing new for a screen reader to say. */
                  role={isMobile ? "status" : undefined}
                  aria-label={isMobile ? (isDirty ? "יש שינויים שלא נשמרו" : "נשמר") : undefined}
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "6px",
                    fontSize: "12.5px",
                    whiteSpace: "nowrap",
                    flex: { xs: "none", md: "0 1 auto" },
                    color: isDirty ? v2.warn : v2.ink3,
                    marginInlineStart: { xs: "auto", md: 0 },
                  }}
                >
                  {isDirty && (
                    <Box
                      sx={{ width: "6px", height: "6px", borderRadius: "50%", background: "currentColor", flex: "none" }}
                    />
                  )}
                  {/* On a phone the dot says it: the sentence and the save button do not both fit
                      beside a menu name, and of the two the button is the one you press. */}
                  <Box component="span" sx={{ display: { xs: "none", md: "inline" } }}>
                    {isDirty ? "יש שינויים שלא נשמרו" : "נשמר"}
                  </Box>
                </Box>
                <PrimaryButton
                  onClick={saveWholeMenu}
                  disabled={!isDirty || saving}
                  sx={{
                    height: { xs: "38px", md: "30px" },
                    padding: "0 12px",
                    fontSize: "13px",
                    flex: { xs: "none", md: "0 1 auto" },
                  }}
                >
                  <SaveIcon sx={{ fontSize: 15 }} />
                  {saving ? "שומר…" : "שמור תפריט"}
                </PrimaryButton>
              </>
            )}
          </Box>

          {/* The targets bar does not pin on a phone, so this does — the same question, in a
              thirtieth of the height, and a tap away from the grid it summarises. */}
          {isMobile && builderOpen && showsMealBuilder && !calculatorOpen && (
            <Box sx={{ paddingBottom: "7px" }}>
              <TargetsMiniStrip totals={totals} targets={menu.targets} onExpand={scrollToTop} />
            </Box>
          )}
        </Box>

        {/* What the menu IS and what it APPLIES TO. Configuration, set once — so on a phone it
            scrolls away with the rest of the page instead of costing four rows of chrome at every
            scroll position. Above md these controls stay in the header, where they have always been. */}
        {isMobile && (menuTypeChip || inactiveChip) && (
          <Box
            data-testid="menu-settings-row"
            sx={{ display: "flex", alignItems: "center", gap: "10px", rowGap: "10px", flexWrap: "wrap" }}
          >
            {menuTypeChip}
            {inactiveChip}
            {daysControl}
          </Box>
        )}

        {/* step 1 — calculator */}
        {calculatorOpen ? (
          <CalculatorPanel
            state={calculator}
            onChange={setCalculator}
            onConfirm={builderOpen ? applyCalculator : () => createMenuStep("DETAILED_PLAN")}
            confirmLabel={builderOpen ? "החל יעדים" : "התאם תפריט תזונה"}
            secondaryActions={
              builderOpen
                ? []
                : [
                    { label: "התאם ערכים יומיים (macros)", onClick: () => createMenuStep("MACRO_GOALS") },
                    { label: "העלה תפריט PDF", onClick: () => createMenuStep("PDF_PLAN") },
                  ]
            }
            onCancel={builderOpen ? cancelCalculator : undefined}
            /*
              Skipping is a way to CREATE a menu, so it is offered only on the way in. Once the
              menu exists the same thing is one button away in the targets bar.

              The same three kinds as the confirm end, because walking past the calculator says
              nothing about WHICH menu this is. It used to land on a detailed menu no matter what,
              so a trainer who had a PDF in hand and no interest in a calculation had to fill the
              formula in anyway just to reach "העלה תפריט PDF".
            */
            skipActions={
              builderOpen
                ? []
                : (["DETAILED_PLAN", "MACRO_GOALS", "PDF_PLAN"] as const).map((menuType) => ({
                    label: MENU_TYPE_LABELS[menuType],
                    onClick: () => createMenuStep(menuType, { skipCalculator: true }),
                  }))
            }
            /* "החל יעדים" needs something to apply; creating a menu does not. */
            requireTdee={builderOpen}
          />
        ) : (
          /* On a detailed menu these same three things — the heading, the targets and the
             way back into the calculator — are folded into the head of the macro grid
             below, which is where a trainer looks for them anyway. A card of its own is
             only needed when there is no macro grid to fold them into. */
          !showsMealBuilder && (
            <Card sx={{ padding: "12px 18px" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
                <Box sx={{ fontSize: "13.5px", fontWeight: 600, color: v2.ink }}>ערכים תזונתיים מומלצים</Box>
                <Box sx={{ fontSize: "13px", color: v2.ink3, ...nums }}>{goalSummary}</Box>
                <Box sx={{ marginInlineStart: "auto" }}>{targetsActions}</Box>
              </Box>
            </Card>
          )
        )}

        {/* step 2 — the builder */}
        {builderOpen && showsMealBuilder && (
          /* The targets and the add-meal actions are handed to MealsList as ONE pinned block
             rather than rendered as siblings above it: two separately-sticky elements would need
             the first's height as a constant to offset the second, and that height changes with
             the viewport. `stickyTop` is the measured header above, so the block comes to rest
             against it instead of sliding underneath — which is what used to slice its numbers
             row off. */
          <MealsList
            meals={menu.meals}
            onChange={(meals) => setMenu((prev) => ({ ...prev, meals }))}
            onNotify={setNotice}
            stickyTop={headerHeight}
            stickyHeader={
              <TargetsSummaryBar
                totals={totals}
                targets={menu.targets}
                bodyWeightKg={bodyWeightKg}
                /* In follow mode there IS no recommendation: the targets are the menu, so a
                   "ערכים תזונתיים מומלצים" heading over a line restating the totals says the same
                   thing twice — and on an empty menu it says it as a row of zeroes. The chip in
                   the action slot is the whole of what the head has to say there. */
                title={targetsFollowMenu ? undefined : "ערכים תזונתיים מומלצים"}
                goalSummary={targetsFollowMenu ? undefined : goalSummary}
                action={calculatorOpen ? undefined : targetsActions}
              />
            }
          />
        )}

        {builderOpen && !showsMealBuilder && (
          <>
            {/* a PDF menu leads with the file; the targets are its daily summary */}
            {isPdfMenu && (
              <MenuPdfSection
                pdfUrl={menu.pdfUrl}
                pdfName={menu.pdfName}
                onChange={(file) => setMenu((prev) => ({ ...prev, ...file }))}
              />
            )}
            <MacroGoalsView
              targets={menu.targets}
              summaryOnly={isPdfMenu}
              recommended={derived.targets}
              onChange={(targets) => setMenu((prev) => ({ ...prev, targets }))}
              qualityProteinPct={menu.qualityProteinPct ?? 0}
              onQualityProteinChange={(qualityProteinPct) =>
                setMenu((prev) => ({ ...prev, qualityProteinPct }))
              }
            />
            {/* Meals on a menu that does not show them: a legacy menu whose type was switched back
                when that was still possible, or one seeded from a template. They are kept and
                saved, simply not part of what this kind of menu is — saying so is better than
                letting a trainer wonder where the meals they can see in the list went. */}
            {menu.meals.length > 0 && (
              <Card sx={{ padding: "12px 16px" }}>
                <Box sx={{ fontSize: "12px", color: t.amberText }}>
                  {menu.meals.length} הארוחות שנבנו בתפריט נשמרות, אך אינן מוצגות בתפריט מסוג זה.
                </Box>
              </Card>
            )}
          </>
        )}

        {/* supplements, water and the note belong to the menu, so they show for ALL types */}
        {builderOpen && (
          <>
            <SupplementsSection
              supplements={menu.supplements || []}
              onChange={(supplements) => setMenu((prev) => ({ ...prev, supplements }))}
            />
            <WaterGoalSection
              waterGoalMl={menu.waterGoalMl}
              onChange={(waterGoalMl) => setMenu((prev) => ({ ...prev, waterGoalMl }))}
            />
            <MenuNotes
              value={menu.notes || ""}
              onChange={(notes) => setMenu((prev) => ({ ...prev, notes }))}
              visible={menu.notesVisible}
              onVisibilityChange={(notesVisible) => setMenu((prev) => ({ ...prev, notesVisible }))}
            />
          </>
        )}

        <ConfirmDialog
          open={leaveOpen}
          onClose={() => setLeaveOpen(false)}
          title="לצאת בלי לשמור?"
          message="יש בתפריט שינויים שלא נשמרו. אם תצא עכשיו הם יאבדו."
          confirmLabel="צא בלי לשמור"
          cancelLabel="הישאר בתפריט"
          extraAction={{
            label: "שמור וצא",
            // Awaited, not fired-and-forgotten: leaving mid-request is exactly how a save gets
            // abandoned, and the trainer would walk away believing the menu was stored.
            onClick: () => {
              void saveWholeMenu().then(leaveNow);
            },
          }}
          onConfirm={leaveNow}
        />

        {/*
          A failed save is the one error on this page that must not be transient: the menu exists
          only in this tab, so the message has to stay on screen until the trainer does something
          about it rather than fading after three seconds.
        */}
        {saveError && (
          <BuilderToast tone="error" testId="menu-save-error" onDismiss={() => setSaveError("")}>
            {saveError}
          </BuilderToast>
        )}

        {notice && <BuilderToast onDismiss={() => setNotice("")}>{notice}</BuilderToast>}

      </Box>
    </RTL>
  );
};

/**
 * Loads the menu, then hands a guaranteed one to the editor.
 *
 * The split exists because the editor's state is seeded from the menu — `useState` initialisers run
 * once, so an editor mounted before the fetch lands would keep the empty menu it started with and
 * quietly overwrite the real one on the next save. Waiting here is the only place that cannot
 * happen.
 */
const MenuBuilderV2Page = () => {
  const navigate = useNavigate();
  const { userId = "", menuId = "new" } = useParams();
  const [searchParams] = useSearchParams();
  const templateId = searchParams.get("templateId");

  const isNew = menuId === "new";
  const { menu, loading, error } = useMenuV2(isNew ? undefined : menuId);

  if (loading) {
    return (
      <RTL>
        <Box
          data-testid="menu-loading"
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 1.5,
            minHeight: "60vh",
          }}
        >
          <CircularProgress size={28} sx={{ color: v2.brand }} />
          <Box sx={{ fontSize: 13, color: v2.ink3 }}>טוען תפריט…</Box>
        </Box>
      </RTL>
    );
  }

  // A menu that will not load must not fall through to a blank builder: the trainer would start
  // editing what looks like their menu and save an empty one over it.
  if (!isNew && (error || !menu)) {
    return (
      <RTL>
        <Box
          data-testid="menu-load-error"
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 1.5,
            minHeight: "60vh",
          }}
        >
          <Box sx={{ fontSize: 15, fontWeight: 600, color: v2.over }}>לא הצלחנו לטעון את התפריט</Box>
          <Box sx={{ fontSize: 13, color: v2.ink3 }}>ייתכן שהתפריט נמחק, או שאין חיבור לשרת</Box>
          <TextButton onClick={() => navigate(`/app/clients/${userId}/nutrition-v2`)}>
            חזרה לתפריטים
          </TextButton>
        </Box>
      </RTL>
    );
  }

  return <MenuBuilderV2Editor userId={userId} existing={menu} templateId={templateId} />;
};

export default MenuBuilderV2Page;
